import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PenLine, ShieldCheck, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { SignaturePad } from "@/components/SignaturePad";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";

export default function SignDocument() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [sigImage, setSigImage] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sign/${token}`);
        setDoc(data);
        if (["signed", "awaiting_countersign", "countersigned"].includes(data.status)) {
          setSigned({ signer_name: data.signer_name, signed_at: data.signed_at, status: data.status });
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "This signing link is invalid or has expired.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const sign = async () => {
    if (!name.trim()) return toast.error("Please type your full name to sign.");
    setSigning(true);
    try {
      const { data } = await api.post(`/sign/${token}/sign`, { signer_name: name.trim(), signature_image: sigImage });
      setSigned({ signer_name: data.signer_name || name.trim(), signed_at: data.signed_at, status: data.status });
      toast.success("Document signed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not sign"); }
    finally { setSigning(false); }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Logo />
          <Badge variant="secondary" className="gap-1 font-normal"><ShieldCheck className="h-3 w-3" /> Secure signing</Badge>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : error ? (
          <Card className="rounded-xl border p-10 text-center" data-testid="sign-error">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-primary"><FileSignature className="h-6 w-6" /></div>
            <h1 className="h-display text-lg font-semibold text-foreground">Link unavailable</h1>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="sign-document">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{doc.company_name}</p>
              <h1 className="h-display text-2xl font-semibold text-foreground">{doc.title}</h1>
              {doc.message && <p className="mt-2 rounded-lg border-l-4 border-primary bg-card px-4 py-2 text-sm text-muted-foreground">{doc.message}</p>}
            </div>

            <Card className="rounded-xl border bg-card p-8">
              <p className="mb-4 text-xs italic text-muted-foreground">{doc.disclaimer}</p>
              <div className="space-y-5">
                {doc.clauses.map((c, i) => (
                  <div key={`${c.title}-${i}`} data-testid={`sign-clause-${i}`}>
                    <h3 className="text-base font-semibold text-primary">{c.title}</h3>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{c.text}</p>
                  </div>
                ))}
              </div>
            </Card>

            {signed ? (
              <Card className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="sign-complete">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <div>
                  <div className="text-sm font-semibold text-emerald-800">Signed by {signed.signer_name}</div>
                  <div className="text-xs text-emerald-700">
                    {formatDateTime(signed.signed_at)} - a copy has been recorded.
                    {signed.status === "awaiting_countersign" && " Awaiting the company's countersignature to finalise."}
                    {signed.status === "countersigned" && " Countersigned by the company - fully executed."}
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="rounded-xl border bg-card p-5" data-testid="sign-form">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><PenLine className="h-4 w-4 text-primary" /> Sign this document</div>
                <Label htmlFor="signer">Your full legal name</Label>
                <Input id="signer" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sam Developer" className="mt-1" data-testid="signer-name-input" />
                <div className="mt-4">
                  <Label>Your signature</Label>
                  <div className="mt-1"><SignaturePad name={name} onChange={setSigImage} /></div>
                </div>
                <Button onClick={sign} disabled={signing || !name.trim()} className="mt-4 w-full gap-2 sm:w-auto" data-testid="sign-submit">
                  {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} Adopt & sign
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">By signing you adopt this as your legally binding electronic signature. It is stamped with a timestamp, IP address and verification ID.</p>
              </Card>
            )}
          </div>
        )}
      </main>
      <TenXBrandFooter context="sign-document" />
    </div>
  );
}
